import { logger } from '@/lib/logger'

export interface ReleaseNote {
  slug: string
  version: string
  date: string
  title: string
  type?: string
  content: string
  rawContent: string
}

interface Frontmatter {
  version: string
  releaseDate: string
  title: string
  type?: string
}

/**
 * Parses frontmatter from markdown content
 *
 * Extracts YAML frontmatter between --- delimiters and returns the parsed
 * metadata along with the content without frontmatter.
 *
 * @param content - The markdown content
 * @returns Parsed frontmatter and content without frontmatter
 */
function parseFrontmatter(content: string): {
  frontmatter: Frontmatter | null
  content: string
} {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/
  const match = content.match(frontmatterRegex)

  if (!match) {
    return { frontmatter: null, content }
  }

  const frontmatterText = match[1]
  const contentWithoutFrontmatter = match[2]

  // Parse YAML-like frontmatter
  const frontmatter: Partial<Frontmatter> = {}
  const lines = frontmatterText.split('\n')

  for (const line of lines) {
    const [key, ...valueParts] = line.split(':')
    if (key && valueParts.length > 0) {
      const value = valueParts.join(':').trim()
      frontmatter[key.trim() as keyof Frontmatter] = value
    }
  }

  if (!frontmatter.version || !frontmatter.releaseDate || !frontmatter.title) {
    return { frontmatter: null, content }
  }

  return {
    frontmatter: frontmatter as Frontmatter,
    content: contentWithoutFrontmatter
  }
}

/**
 * Extracts metadata from markdown frontmatter
 *
 * Parses release note files to extract version, date, and title from
 * frontmatter or filename as fallback.
 *
 * @param filename - The markdown filename
 * @param content - The markdown content
 * @returns Parsed metadata or null if parsing fails
 */
function parseReleaseNoteMetadata(filename: string, content: string) {
  const { frontmatter, content: contentWithoutFrontmatter } = parseFrontmatter(content)

  if (frontmatter) {
    return {
      version: frontmatter.version,
      date: frontmatter.releaseDate,
      title: frontmatter.title,
      type: frontmatter.type,
      content: contentWithoutFrontmatter
    }
  }

  // Fallback: Extract from filename (legacy format)
  const match = filename.match(/release-notes-(.+)-(\d{4}-\d{2}-\d{2})\.md$/)

  if (!match) {
    return null
  }

  const version = match[1]
  const date = match[2]

  // Extract title from first H1 heading
  const titleMatch = content.match(/^#\s+(.+)$/m)
  const title = titleMatch ? titleMatch[1] : `Release Notes - ${version}`

  return { version, date, title, type: undefined, content }
}

/**
 * Retrieves all release notes from the public folder
 *
 * Fetches and parses markdown files containing release information.
 * Returns an array of release notes sorted by date (newest first).
 *
 * @returns Array of release notes sorted by date
 */
export async function getReleaseNotes(): Promise<ReleaseNote[]> {
  try {
    // Fetch the list of markdown files from the public directory
    const response = await fetch('/release-notes-data/index.json')
    if (!response.ok) {
      logger.error('Failed to fetch release notes index')
      return []
    }

    const files: string[] = await response.json()
    const releaseNotes: ReleaseNote[] = []

    for (const file of files) {
      const contentResponse = await fetch(`/release-notes-data/${file}`)
      if (!contentResponse.ok) continue

      const content = await contentResponse.text()
      const metadata = parseReleaseNoteMetadata(file, content)

      if (metadata) {
        releaseNotes.push({
          slug: file.replace('.md', ''),
          version: metadata.version,
          date: metadata.date,
          title: metadata.title,
          type: metadata.type,
          content: metadata.content,
          rawContent: content
        })
      }
    }

    // Sort by date (newest first)
    return releaseNotes.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    )
  } catch (error) {
    logger.error('Error reading release notes', error)
    return []
  }
}

/**
 * Retrieves a specific release note by slug
 *
 * Loads and parses a single release note markdown file by its slug identifier.
 *
 * @param slug - The release note slug (filename without extension)
 * @returns The release note or null if not found
 */
export async function getReleaseNoteBySlug(slug: string): Promise<ReleaseNote | null> {
  try {
    const filename = `${slug}.md`
    const response = await fetch(`/release-notes-data/${filename}`)

    if (!response.ok) {
      return null
    }

    const content = await response.text()
    const metadata = parseReleaseNoteMetadata(filename, content)

    if (!metadata) {
      return null
    }

    return {
      slug,
      version: metadata.version,
      date: metadata.date,
      title: metadata.title,
      type: metadata.type,
      content: metadata.content,
      rawContent: content
    }
  } catch (error) {
    logger.error(`Error reading release note ${slug}`, error)
    return null
  }
}

/**
 * Retrieves the latest version from release notes
 *
 * Parses all release notes and returns the version of the most recent release
 * based on the release date.
 *
 * @returns The latest version string or null if no release notes found
 */
export async function getLatestVersion(): Promise<string | null> {
  try {
    const releaseNotes = await getReleaseNotes()

    if (releaseNotes.length === 0) {
      return null
    }

    // getReleaseNotes already sorts by date (newest first)
    return releaseNotes[0].version
  } catch (error) {
    logger.error('Error getting latest version', error)
    return null
  }
}
